import React, { useState, useEffect } from 'react';
import { financialReportsAPI, businessesAPI, taxExportAPI, booksDownloadAPI } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary'; import { drillHref, DrillLink } from '../features/drilldown/drillLinks';
import { formatApiError } from '../utils/errorMessage';
import { todayLocal, localDateKey } from '../utils/localDate';

/**
 * Reports Page
 * Single Responsibility: Display financial reports (P&L, Balance Sheet, Cash Flow, Tax Export)
 */
const Reports = ({ selectedBusiness }) => {
  const [activeReport, setActiveReport] = useState('profit-loss');
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(selectedBusiness || '');
  const [dateRange, setDateRange] = useState({
    start_date: localDateKey(new Date(new Date().getFullYear(), 0, 1)), // Jan 1
    end_date: todayLocal() // Today
  });
  const [asOfDate, setAsOfDate] = useState(todayLocal());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // DBA / division split for the P&L. divisionOnly: the caller is limited to
  // their divisions, so only the P&L is offered.
  const [divisionInfo, setDivisionInfo] = useState({ division_only: false, divisions: [] });
  const [selectedDivision, setSelectedDivision] = useState('');
  const divisionOnly = Boolean(divisionInfo.division_only);

  useEffect(() => {
    loadBusinesses();
  }, []);

  useEffect(() => {
    let active = true;
    setSelectedDivision('');
    if (!selectedBusinessId) {
      setDivisionInfo({ division_only: false, divisions: [] });
      return undefined;
    }
    financialReportsAPI.getDivisions({ business_id: selectedBusinessId })
      .then((res) => {
        if (!active) return;
        const info = res.data || {};
        setDivisionInfo({ division_only: Boolean(info.division_only), divisions: Array.isArray(info.divisions) ? info.divisions : [] });
        if (info.division_only) setActiveReport('profit-loss');
      })
      .catch(() => { if (active) setDivisionInfo({ division_only: false, divisions: [] }); });
    return () => { active = false; };
  }, [selectedBusinessId]);

  useEffect(() => {
    if (activeReport && (activeReport === 'profit-loss' || activeReport === 'cash-flow')) {
      loadReport();
    } else if (activeReport === 'balance-sheet') {
      loadBalanceSheet();
    }
  }, [activeReport, dateRange, selectedBusinessId, asOfDate, selectedDivision]);

  const loadBusinesses = async () => {
    try {
      const res = await businessesAPI.getAll();
      setBusinesses(res.data);
    } catch (err) {
      console.error('Failed to load businesses:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
    }
  };

  const loadReport = async () => {
    if (!dateRange.start_date || !dateRange.end_date) return;

    setLoading(true);
    setError('');

    try {
      const params = {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        ...(selectedBusinessId ? { business_id: selectedBusinessId } : {}),
        ...(activeReport === 'profit-loss' && selectedBusinessId && selectedDivision ? { state_registration_id: selectedDivision } : {})
      };

      let data;
      if (activeReport === 'profit-loss') {
        const res = await financialReportsAPI.getProfitLoss(params);
        data = res.data;
      } else if (activeReport === 'cash-flow') {
        const res = await financialReportsAPI.getCashFlow(params);
        data = res.data;
      }

      setReportData(data);
    } catch (err) {
      setError(formatApiError(err, 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  };

  const loadBalanceSheet = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        as_of_date: asOfDate,
        ...(selectedBusinessId ? { business_id: selectedBusinessId } : {})
      };

      const res = await financialReportsAPI.getBalanceSheet(params);
      setReportData(res.data);
    } catch (err) {
      setError(formatApiError(err, 'Failed to load balance sheet'));
    } finally {
      setLoading(false);
    }
  };

  const downloadBooks = async (list) => {
    try {
      const response = await booksDownloadAPI.download(list);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${list}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(formatApiError(err, 'Failed to download'));
    }
  };

  const handleTaxExport = async (format) => {
    if (!dateRange.start_date || !dateRange.end_date) {
      alert('Please select a date range');
      return;
    }

    try {
      const params = {
        format,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        ...(selectedBusinessId ? { business_id: selectedBusinessId } : {})
      };

      const response = await taxExportAPI.export(params);
      const blob = new Blob([response.data], { type: format === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tax-export-${dateRange.start_date}-to-${dateRange.end_date}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(formatApiError(err, 'Failed to export tax data'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <ErrorBoundary section="Reports">
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Financial Reports</h1>
          <p className="text-gray-600 mt-1">Generate comprehensive financial reports and tax exports</p>
          {!divisionOnly && <div className="flex flex-wrap gap-2 mt-3">
            {['accounts', 'transactions', 'budgets', 'bills', 'assets'].map((list) => (
              <button
                key={list}
                type="button"
                onClick={() => downloadBooks(list)}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Download {list}
              </button>
            ))}
          </div>}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" role="alert">
            {error}
          </div>
        )}

        {/* Report Type Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'profit-loss', name: 'Profit & Loss', icon: '📈' },
                { id: 'balance-sheet', name: 'Balance Sheet', icon: '💰' },
                { id: 'cash-flow', name: 'Cash Flow', icon: '💸' },
                { id: 'tax-export', name: 'Tax Export', icon: '📑' }
              ].filter((report) => !divisionOnly || report.id === 'profit-loss').map((report) => (
                <button
                  key={report.id}
                  onClick={() => setActiveReport(report.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeReport === report.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{report.icon}</span>
                  {report.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Filters */}
            <div className="mb-6 flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business</label>
                <select
                  value={selectedBusinessId}
                  onChange={(e) => setSelectedBusinessId(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">All Businesses</option>
                  {businesses.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>

              {activeReport === 'profit-loss' && selectedBusinessId && divisionInfo.divisions.length > 0 && (
                <div>
                  <label htmlFor="report-division" className="block text-sm font-medium text-gray-700 mb-1">DBA / division</label>
                  <select
                    id="report-division"
                    value={selectedDivision}
                    onChange={(e) => setSelectedDivision(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">{divisionOnly ? 'All my divisions' : 'Whole business'}</option>
                    {!divisionOnly && <option value="parent">Business only (no DBA)</option>}
                    {divisionInfo.divisions.map((d) => (
                      <option key={d.id} value={String(d.id)}>{d.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {(activeReport === 'profit-loss' || activeReport === 'cash-flow' || activeReport === 'tax-export') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={dateRange.start_date}
                      onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={dateRange.end_date}
                      onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                </>
              )}

              {activeReport === 'balance-sheet' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">As Of Date</label>
                  <input
                    type="date"
                    value={asOfDate}
                    onChange={(e) => setAsOfDate(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2"
                  />
                </div>
              )}
            </div>

            {/* Report Content */}
            {loading ? (
              <div className="text-center py-12">
                <div className="text-gray-500">Loading report...</div>
              </div>
            ) : (
              <>
                {/* Profit & Loss */}
                {activeReport === 'profit-loss' && reportData && (
                  <div className="space-y-6">
                    {(reportData.provisional_starts || []).length > 0 && (
                      <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="provisional-starts">
                        Provisional starting balance: {reportData.provisional_starts.map((p) => p.account_name + (p.opening_balance_note ? ` (${p.opening_balance_note})` : '')).join(', ')}
                      </div>
                    )}
                    <p className="text-sm text-gray-600">Category sums</p>
                    <div className="bg-gray-50 p-4 rounded">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-semibold text-gray-900">Net Income</h3>
                          <p className="text-2xl font-bold text-blue-600"><DrillLink to={drillHref({ metric: 'net', start: dateRange.start_date, end: dateRange.end_date, business: selectedBusinessId })}>{formatCurrency(reportData.totals?.net || 0)}</DrillLink></p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Total Income</div>
                          <div className="text-lg font-semibold text-green-600"><DrillLink to={drillHref({ metric: 'income', start: dateRange.start_date, end: dateRange.end_date, business: selectedBusinessId })}>{formatCurrency(reportData.totals?.income || 0)}</DrillLink></div>
                          <div className="text-sm text-gray-600 mt-2">Total Expenses</div>
                          <div className="text-lg font-semibold text-red-600"><DrillLink to={drillHref({ metric: 'expenses', start: dateRange.start_date, end: dateRange.end_date, business: selectedBusinessId })}>{formatCurrency(reportData.totals?.expenses || 0)}</DrillLink></div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Income</h3>
                        <div className="space-y-2">
                          {reportData.income?.map((item, idx) => (
                            <div key={item.category_id || `income-${item.category_name || idx}`} className="flex justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm">{item.category_name}</span>
                              <span className="text-sm font-semibold text-green-600"><DrillLink to={drillHref({ metric: 'income', start: dateRange.start_date, end: dateRange.end_date, business: selectedBusinessId, category: item.category_id ?? 'uncategorized', type: 'income' })}>{formatCurrency(item.income)}</DrillLink></span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Expenses</h3>
                        <div className="space-y-2">
                          {reportData.expenses?.map((item, idx) => (
                            <div key={item.category_id || `expense-${item.category_name || idx}`} className="flex justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm">{item.category_name}</span>
                              <span className="text-sm font-semibold text-red-600"><DrillLink to={drillHref({ metric: 'expenses', start: dateRange.start_date, end: dateRange.end_date, business: selectedBusinessId, category: item.category_id ?? 'uncategorized', type: 'expense' })}>{formatCurrency(item.expenses)}</DrillLink></span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Balance Sheet */}
                {activeReport === 'balance-sheet' && reportData && (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-600">Category sums</p>
                    <div className="bg-gray-50 p-4 rounded">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-semibold text-gray-900">Total Equity</h3>
                          <p className="text-2xl font-bold text-blue-600">{formatCurrency(reportData.equity || 0)}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Total Assets</div>
                          <div className="text-lg font-semibold text-green-600">{formatCurrency(reportData.assets?.total || 0)}</div>
                          <div className="text-sm text-gray-600 mt-2">Total Liabilities</div>
                          <div className="text-lg font-semibold text-red-600">{formatCurrency(reportData.liabilities?.total || 0)}</div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Assets</h3>
                        <div className="space-y-2">
                          {reportData.assets?.accounts?.map((account, idx) => (
                            <div key={account.account_id || account.id || `asset-account-${idx}`} className="flex justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm">{account.account_name}</span>
                              <span className="text-sm font-semibold text-green-600">{formatCurrency(account.current_balance)}</span>
                            </div>
                          ))}
                          {reportData.assets?.assets > 0 && (
                            <div className="flex justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm">Other Assets</span>
                              <span className="text-sm font-semibold text-green-600">{formatCurrency(reportData.assets.assets)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Liabilities</h3>
                        <div className="space-y-2">
                          {reportData.liabilities?.accounts?.map((account, idx) => (
                            <div key={account.account_id || account.id || `liability-account-${idx}`} className="flex justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm">{account.account_name}</span>
                              <span className="text-sm font-semibold text-red-600">{formatCurrency(account.current_balance)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Cash Flow */}
                {activeReport === 'cash-flow' && reportData && (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-600">Category sums</p>
                    <div className="bg-gray-50 p-4 rounded">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-semibold text-gray-900">Net Cash Flow</h3>
                          <p className={`text-2xl font-bold ${reportData.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(reportData.netCashFlow || 0)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white border rounded p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Operating Activities</h4>
                        <div className="text-sm text-gray-600">Income: {formatCurrency(reportData.operating?.income || 0)}</div>
                        <div className="text-sm text-gray-600">Expenses: {formatCurrency(reportData.operating?.expenses || 0)}</div>
                        <div className="text-lg font-semibold text-blue-600 mt-2">Net: {formatCurrency(reportData.operating?.net || 0)}</div>
                      </div>
                      <div className="bg-white border rounded p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Investing Activities</h4>
                        <div className="text-sm text-gray-600">Purchases: {formatCurrency(reportData.investing?.purchases || 0)}</div>
                        <div className="text-sm text-gray-600">Sales: {formatCurrency(reportData.investing?.sales || 0)}</div>
                        <div className="text-lg font-semibold text-blue-600 mt-2">Net: {formatCurrency(reportData.investing?.net || 0)}</div>
                      </div>
                      <div className="bg-white border rounded p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Financing Activities</h4>
                        <div className="text-sm text-gray-600">Loans Received: {formatCurrency(reportData.financing?.loans_received || 0)}</div>
                        <div className="text-sm text-gray-600">Loan Payments: {formatCurrency(reportData.financing?.loan_payments || 0)}</div>
                        <div className="text-lg font-semibold text-blue-600 mt-2">Net: {formatCurrency(reportData.financing?.net || 0)}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tax Export */}
                {activeReport === 'tax-export' && (
                  <div className="text-center py-12">
                    <h3 className="text-xl font-semibold mb-4">Export Tax Data</h3>
                    <p className="text-gray-600 mb-6">Export your financial data for tax preparation</p>
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={() => handleTaxExport('csv')}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Export as CSV
                      </button>
                      <button
                        onClick={() => handleTaxExport('pdf')}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Export as PDF
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Reports;
